import * as React from 'react'
import styled from 'styled-components'
import { Style } from '../../../styles'
import { PeppolController } from '../../../controllers'
import { CurrentUser, QRCData, RemittanceInformationType, UBLData, UBLExtractionData } from '../../../types'
import { useTranslation } from 'react-i18next'
import NumberFormatter from '../../../utilities/NumberFormatter'
import { useSelector, useStore } from 'react-redux'
import { AppState } from '../../../store'
import PaymentHelper from '../../../helpers/PaymentHelper'
import QRCode from '../../QR/QRCode'
import OgmHelper from '../../../helpers/OgmHelper'
import moment from '../../../utilities/Moment'
import UnknownPreview from './UnknownPreview'

const Container = styled.div`
  height: 100%;
  width: 100%;
  background: white;
  padding: 20px;
  overflow: auto;

  canvas {
    margin-left: -20px;
  }
`

const UnknownPreviewContainer = styled.div`
  height: 100%;
  width: 100%;
  padding: 20px;
`

const InvoiceDetails = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 20px;

  > div {
    display: flex;
    flex-direction: column;
    flex: 1;

    label {
      font-weight: 700;
      font-size: 14px;
      text-transform: none;
      margin-bottom: 4px;
    }

    > div {

    }
  }
`

const Title = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: rgb(15, 23, 42);
  margin-bottom: 10px;
`

const PaymentHeader = styled.div`
  display: flex;
  flex-direction: row;

  > div {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  label {
    font-size: 14px;
    margin-bottom: 0;
  }
`

const Amount = styled.div`
  font-size: 36px;
  font-weight: 700;
`

const PaymentDetails = styled.div`
  display: flex;
  flex-direction: column;
`

const PaymentDetailsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const PaymentDetailsLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  flex: 1;
`

const PaymentDetailsValue = styled.div`
  flex: 1;
`

const Table = styled.table`
  border: 1px solid #cbd5e1;
  border-collapse: collapse;
  width: 100%;
  margin-top: 40px;

  thead {
    background: rgb(248, 250, 252);

    th {
      font-size: 14px;
      font-weight: 700;
      text-align: left;
      padding: 8px;
      border: 1px solid #cbd5e1;

      &:last-child {
        text-align: right;
      }
    }
  }

  tbody {
    td {
      padding: 8px;
      border: 1px solid #cbd5e1;
      
      &:last-child {
        text-align: right;
      }
    }
  }
`

interface IProps {
  filename: string
  ubl: string
}

const UBLPreview = (props: IProps) => {
  const { filename, ubl } = props
  const currentUser = useSelector<AppState, CurrentUser>((state: AppState) => state.authentication.currentUser)
  const { workspace: { setting: { number_format, date_format } } } = currentUser
  const { t } = useTranslation()
  const [ublData, setUblData] = React.useState<UBLData>(null)

  const fetchUBLData = async () => {
    try {
      const data = await PeppolController.parse(ubl)
      setUblData(data)
    } catch (ex) {
      console.error(ex)
    }
  }

  React.useEffect(() => {
    fetchUBLData().catch(console.error)
  }, [])

  if (!ublData) return (
    <UnknownPreviewContainer>
      <UnknownPreview
        filename={filename}
      />
    </UnknownPreviewContainer>
  )

  const {
    type,
    supplier,
    number,
    customer,
    total_inclusive,
    currency,
    payment_details,
    issue_date,
    line_items,
  } = ublData

  const getQuickResponseCodeData = (): QRCData => {
    console.log(payment_details)
    return {
      name: supplier?.name || supplier?.legal_name,
      iban: payment_details.account_id,
      amount: Number(total_inclusive),
      currency: currency,
      remittanceInformation: payment_details?.id,
      remittanceInformationType: OgmHelper.validate(OgmHelper.unformat(payment_details?.id || '')) ? RemittanceInformationType.STRUCTURED : RemittanceInformationType.UNSTRUCTURED
    }
  }

  return (
    <Container>
      <Title>{type === 'invoice' ? t('UBLPreview::Invoice') : t('UBLPreview::Creditnote')} {number} </Title>

      <InvoiceDetails>
        <div>
          <label>{t('UBLPreview::From:')}</label>
          <div>{supplier?.name || supplier?.legal_name}</div>
          <div>{supplier?.address?.street}</div>
          <div>{supplier?.address?.postal} {supplier?.address?.city}</div>
        </div>

        <div>
          <label>{t('UBLPreview::To:')}</label>
          <div>{customer?.name || customer?.legal_name}</div>
          <div>{customer?.address?.street}</div>
          <div>{customer?.address?.postal} {customer?.address?.city}</div>
        </div>
      </InvoiceDetails>

      <PaymentHeader>
        <div>
          <label>{t('UBLPreview::Pay:')}</label>
          {total_inclusive && <Amount>{NumberFormatter.formatCurrency(currency, number_format, total_inclusive)}</Amount>}
        </div>

        <div>
          <QRCode
            data={PaymentHelper.quickResponseCodeData(getQuickResponseCodeData())}
            width={200}
          />
        </div>
      </PaymentHeader>

      <PaymentDetails>
        <PaymentDetailsRow>
          <PaymentDetailsLabel>{t('UBLPreview::IBAN:')}</PaymentDetailsLabel>
          <PaymentDetailsValue>{payment_details?.account_id}</PaymentDetailsValue>
        </PaymentDetailsRow>
        <PaymentDetailsRow>
          <PaymentDetailsLabel>{t('UBLPreview::Communication:')}</PaymentDetailsLabel>
          <PaymentDetailsValue>{payment_details?.id ? payment_details?.id : t('UBLPreview::N/A')}</PaymentDetailsValue>
        </PaymentDetailsRow>
        {issue_date && <PaymentDetailsRow>
          <PaymentDetailsLabel>{t('UBLPreview::Issued on:')}</PaymentDetailsLabel>
          <PaymentDetailsValue>{issue_date}</PaymentDetailsValue>
        </PaymentDetailsRow>}
        {type === 'invoice' && ublData.due_date && <PaymentDetailsRow>
          <PaymentDetailsLabel>{t('UBLPreview::Due on:')}</PaymentDetailsLabel>
          <PaymentDetailsValue>{moment(ublData.due_date).format(date_format)} ({moment(ublData.due_date).fromNow()})</PaymentDetailsValue>
        </PaymentDetailsRow>}
      </PaymentDetails>

      <Table>
        <thead>
          <tr>
            <th>{t('UBLPreview::Description:')}</th>
            <th>{t('UBLPreview::Amount')}</th>
          </tr>
        </thead>
        <tbody>
          {line_items?.map((item) => {
            return (
              <tr>
                <td>{item.description}</td>
                <td>{NumberFormatter.formatCurrency(currency, number_format, item.total_amount)}</td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </Container>
  )
}

export default UBLPreview